import { Trans } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  Typography as MuiTypography,
  IconButton as MuiIconButton,
  Tooltip as MuiTooltip,
  ListItem as MuiListItem,
} from "@mui/material";
import {
  Check as MuiCheckIcon,
  Edit as MuiEditIcon,
} from "@mui/icons-material";
import { maskDOB, maskEmailAddress, maskPhoneNumber } from "app/shared/utils";
import Mail from "app/shared/UI/Mail";
import { prospectListingPageStyles } from "./ProspectListingPageStyles";
import { headerStyles, multiLineCell } from "app/shared/UI/sharedStyles";

export const defaultTableColumnHeaders = (t) => [
  {
    field: "mrn",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="LookupProspect.tableColumnMRN" />
      </MuiTypography>
    ),
    flex: 1,
    minWidth: 155,
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>{value}</MuiTypography>
    ),
  },
  {
    field: "firstName",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="LookupProspect.tableColumnFirstName" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>{value}</MuiTypography>
    ),
    flex: 1,
    minWidth: 120,
  },
  {
    field: "lastName",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="LookupProspect.tableColumnLastName" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>{value}</MuiTypography>
    ),
    flex: 1,
    minWidth: 120,
  },
  {
    field: "emails",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="LookupProspect.tableColumnEmail" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>
        {value &&
          value.map((emailRow, emailIdx) => (
            <MuiListItem key={emailIdx}>
              <Mail emailAddress={emailRow}>
                <MuiTooltip title={emailRow} arrow>
                  <span>{maskEmailAddress(emailRow)}</span>
                </MuiTooltip>
              </Mail>
            </MuiListItem>
          ))}
      </MuiTypography>
    ),
    flex: 1,
    minWidth: 330,
    sortable: false,
  },
  {
    field: "phones",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="LookupProspect.tableColumnPhone" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography>
        {value &&
          value.map((phoneRow, phoneIdx) => (
            <MuiTypography sx={multiLineCell}>
              <MuiTooltip title={phoneRow} arrow>
                <span>{maskPhoneNumber(phoneRow)}</span>
              </MuiTooltip>
            </MuiTypography>
          ))}
      </MuiTypography>
    ),
    flex: 1,
    minWidth: 130,
    sortable: false,
  },
  {
    field: "birthDate",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="LookupProspect.tableColumnDOB" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>
        <MuiTooltip title={value} arrow>
          <span>{value && maskDOB(value)}</span>
        </MuiTooltip>
      </MuiTypography>
    ),
    flex: 1,
    minWidth: 100,
  },
  {
    field: "addresses",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="LookupProspect.tableColumnAddress" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>
        {value &&
          value.map((addressRow, addressIdx) => (
            <MuiTooltip title={addressRow} arrow>
              <MuiTypography
                componnent="span"
                variant="body2"
                sx={prospectListingPageStyles.customNoteTableCell}
              >
                {addressRow}
              </MuiTypography>
            </MuiTooltip>
          ))}
      </MuiTypography>
    ),
    flex: 1,
    minWidth: 110,
    sortable: false,
  },
  {
    field: "note",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="LookupProspect.tableColumnNote" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>
        <MuiTooltip title={value} arrow>
          <span style={prospectListingPageStyles.customNoteTableCell}>
            {value}
          </span>
        </MuiTooltip>
      </MuiTypography>
    ),
    flex: 1,
    minWidth: 100,
    sortable: false,
  },
  {
    field: "optOut",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="LookupProspect.tableColumnOptOut" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>
        {value && <MuiCheckIcon color="primary" fontSize="small" />}
      </MuiTypography>
    ),
    flex: 1,
    minWidth: 80,
    sortable: false,
  },
  {
    field: "concern",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="LookupProspect.tableColumnConcernOnFile" />
      </MuiTypography>
    ),
    renderCell: ({ value }) => (
      <MuiTypography sx={multiLineCell}>
        {value && <MuiCheckIcon color="primary" fontSize="small" />}
      </MuiTypography>
    ),
    flex: 1,
    minWidth: 100,
    sortable: false,
  },
  {
    field: "actions",
    headerAlign: "left",
    renderHeader: () => (
      <MuiTypography style={headerStyles}>
        <Trans i18nKey="LookupProspect.tableColumnAction" />
      </MuiTypography>
    ),
    type: "actions",
    align: "left",
    flex: 1,
    minWidth: 100,
    renderCell: ({ row }) => {
      return (
        <MuiTooltip title={t(`LookupProspect.editProspect`)} arrow>
          <RouterLink
            to={`/prospects/${row.mrn}/edit`}
            state={{ prospect: row }}
          >
            <MuiIconButton size="small">
              <MuiEditIcon fontSize="small" />
            </MuiIconButton>
          </RouterLink>
        </MuiTooltip>
      );
    },
  },
];
